import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';

function Avatar({ name }) {
  const initials = (name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div className="w-10 h-10 rounded-full bg-magenta-500 flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
      {initials}
    </div>
  );
}

export default function BHELLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const isManagement = user.role === 'BHEL_MANAGEMENT';
  const isSPOC = user.role === 'BHEL_SPOC';

  const navItems = [
    ...(isManagement ? [{ to: '/bhel/dashboard', label: 'Dashboard', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> }] : []),
    ...(isSPOC ? [{ to: '/bhel/my-location', label: 'My Location', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> }] : []),
    { to: '/bhel/tickets', label: 'Tickets', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg> },
    { to: '/bhel/attendance', label: 'RE Attendance', icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-brand-50">
      <aside className="w-64 flex-shrink-0 flex flex-col" style={{background: 'linear-gradient(160deg, #3D3562 0%, #272244 100%)'}}>
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2L38 40H2L20 2Z" fill="#C1278D"/>
                <circle cx="20" cy="17" r="4" fill="white"/>
                <rect x="17" y="20" width="6" height="10" rx="1" fill="white"/>
              </svg>
            </div>
            <div>
              <p className="text-white font-bold text-base leading-none tracking-tight">PROACTIVE</p>
              <p className="text-purple-300 text-xs mt-0.5 font-medium">BHEL Portal</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-magenta-500 text-white shadow-lg shadow-magenta-900/30'
                    : 'text-purple-200 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={isActive ? 'text-white' : 'text-purple-300 group-hover:text-white transition-colors'}>{item.icon}</span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Raise Ticket — SPOC only */}
        {isSPOC && (
          <div className="px-3 pb-3">
            <button
              onClick={() => navigate('/bhel/tickets/new')}
              className="w-full flex items-center justify-center gap-2 bg-magenta-500 hover:bg-magenta-400 text-white text-sm font-semibold py-2.5 rounded-xl transition-all duration-150 shadow-lg shadow-magenta-900/20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
              Raise a Ticket
            </button>
          </div>
        )}

        {/* User */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <Avatar name={user.full_name || user.email} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user.full_name || 'BHEL User'}</p>
              <p className="text-purple-300 text-xs truncate">{user.role === 'BHEL_SPOC' ? 'BHEL SPOC' : 'BHEL Management'}</p>
            </div>
            <button onClick={() => { dispatch(logout()); navigate('/login'); }} title="Sign out" className="text-purple-300 hover:text-white transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-[#F5F3FC]">
        <Outlet />
      </main>
    </div>
  );
}
